/**
 * Native Android/iOS deep-link handling (Capacitor).
 *
 * PROBLEM (2026 P0 bug): the app loads a REMOTE server URL
 * (`capacitor.config.json` → server.url = https://allurexp.com). When Android
 * opens the app via an App Link like `https://allurexp.com/invite/AB12-CD34`,
 * Capacitor cold-starts the WebView at the ROOT url — the invite path is
 * dropped and React Router never sees it.
 *
 * COLD-START RACE (2026-08 P0): getLaunchUrl() resolves async, so by the time
 * we navigated to the invite, App.js had already redirected to /unlock (passkey
 * cold-launch) or SmartRootRouter had redirected '/' to the role home — both
 * stomped the invite navigation. Warm-start (appUrlOpen) worked fine.
 *
 * FIX:
 *  1. `captureNativeLaunchContext()` is called from index.js BEFORE React
 *     mounts. It reads the launch intent (and, on the very first launch after
 *     a Play Store install, the Install Referrer for deferred deep links) and
 *     buffers the extracted path.
 *  2. App.js AWAITS the capture before deciding on the /unlock redirect.
 *  3. SmartRootRouter checks the buffered path before routing '/' to a home.
 *  4. The moment a deep link is captured we ALSO persist the type-specific
 *     pending id (pendingInviteId / staff token) so the existing post-auth
 *     resume machinery (postLoginRouting / defaultRoute / VerifyEmailPage /
 *     GoogleCompleteProfilePage) routes back to the invite even if every
 *     direct navigation race is lost.
 *
 * SAFETY: we NEVER import '@capacitor/app' at module level — we reach into
 * `window.Capacitor.Plugins.*` at call time. Old APKs without the plugins
 * simply no-op instead of crashing the JS boot chunk (the iter-219 failure).
 */
import {
  storePendingInviteId,
  storePendingStaffInviteToken,
  storePendingHostListingInviteId,
  storePendingHostListingInviteData,
} from './deepLinkHandler';

// Only invite-style paths auto-navigate. Payment intents
// (allurexp://payment-complete) are deliberately excluded — that intent
// exists purely to foreground the app and dismiss the Chrome Custom Tab.
const DEEP_LINK_PATH_RE = /^\/(invite|host-invite|venue-invite|staff-invite)\/.+/;

const LAUNCH_LINK_SS_KEY = 'allure_launch_deep_link';
const REFERRER_SENTINEL_KEY = 'allure_install_referrer_consumed_v1';
// Deep links that have already been honoured in THIS WebView session.
// `window.location.replace()` (used by invite acceptance flows) forces a
// full WebView reload, after which Capacitor's `App.getLaunchUrl()` keeps
// returning the ORIGINAL App Link forever — so without this sentinel the
// cold-start handler would re-navigate the user back to the invite page
// after every acceptance, producing a visible accept→scan→accept loop
// (iter Feb 2026 staff-invite regression). sessionStorage survives the
// reload but clears when the webview process dies, so a genuinely fresh
// cold launch from the launcher icon still honours any pending link.
const CONSUMED_LAUNCH_LINKS_SS_KEY = 'allure_consumed_launch_deep_links_v1';

function readConsumedLaunchLinks() {
  try {
    const raw = window.sessionStorage.getItem(CONSUMED_LAUNCH_LINKS_SS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function isLaunchDeepLinkConsumed(path) {
  if (!path) return false;
  return readConsumedLaunchLinks().includes(path);
}

/**
 * Mark a specific deep-link path as already-honoured so the cold-start
 * handler ignores it on the next WebView reload. Invite acceptance flows
 * MUST call this immediately before any `window.location.replace()` away
 * from the invite entry page — otherwise the native launch URL keeps
 * bouncing the user back onto the accept screen (see note on
 * CONSUMED_LAUNCH_LINKS_SS_KEY above).
 */
export function markLaunchDeepLinkConsumed(path) {
  if (!path) return;
  try {
    const current = readConsumedLaunchLinks();
    if (current.includes(path)) return;
    const next = [...current, path].slice(-16); // bounded, LRU-ish
    window.sessionStorage.setItem(CONSUMED_LAUNCH_LINKS_SS_KEY, JSON.stringify(next));
  } catch {
    /* sessionStorage unavailable — fall through, worst case the loop
       recurs but only in private-mode browsers where native deep links
       don't apply anyway. */
  }
}

export function sanitizeDeepLinkPath(candidate) {
  if (!candidate || typeof candidate !== 'string') return null;
  const trimmed = candidate.trim();
  if (!trimmed.startsWith('/')) return null;
  if (!DEEP_LINK_PATH_RE.test(trimmed)) return null;
  return trimmed;
}

export function extractDeepLinkPath(rawUrl) {
  if (!rawUrl || typeof rawUrl !== 'string') return null;
  let path = null;
  let search = '';
  try {
    if (rawUrl.startsWith('allurexp://')) {
      // Custom scheme fallback: allurexp://invite/AB12-CD34?x=y
      const rest = rawUrl.slice('allurexp://'.length);
      const queryIndex = rest.indexOf('?');
      const before = queryIndex >= 0 ? rest.slice(0, queryIndex) : rest;
      search = queryIndex >= 0 ? rest.slice(queryIndex) : '';
      path = `/${before.replace(/^\/+/, '')}`;
    } else {
      const parsed = new URL(rawUrl);
      path = parsed.pathname;
      search = parsed.search || '';
    }
  } catch {
    return null;
  }
  if (!path || !DEEP_LINK_PATH_RE.test(path)) return null;
  return `${path}${search}`;
}

/**
 * Parse the Play Store install-referrer string for a deferred deep link.
 * The invite gate builds Play links as
 *   ...&referrer=allure_dl%3D%252Finvite%252FAB12-CD34
 * Play stores the (once-decoded) value `allure_dl=%2Finvite%2FAB12-CD34`;
 * URLSearchParams decodes the inner value back to `/invite/AB12-CD34`.
 */
export function extractReferrerDeepLinkPath(referrerString) {
  if (!referrerString || typeof referrerString !== 'string') return null;
  try {
    const params = new URLSearchParams(referrerString);
    return sanitizeDeepLinkPath(params.get('allure_dl'));
  } catch {
    return null;
  }
}

/**
 * Persist the type-specific pending id for a deep-link path so the existing
 * post-auth resume machinery honours it even if direct navigation is lost
 * (user has to log in / register / verify email first).
 */
export function rememberDeepLinkPendingIds(path) {
  try {
    const inviteMatch = path.match(/^\/invite\/([^/?#]+)/);
    if (inviteMatch) {
      storePendingInviteId(inviteMatch[1]);
      return;
    }
    const staffMatch = path.match(/^\/staff-invite\/([^/?#]+)/);
    if (staffMatch) {
      storePendingStaffInviteToken(staffMatch[1]);
      return;
    }
    const hostInviteMatch = path.match(/^\/host-invite\/([^/?#]+)/);
    if (hostInviteMatch) {
      // Belt-and-suspenders: persist the id AND the listing data carried in
      // the query string so the verify-listing prefill works even when every
      // direct navigation race is lost (register → verify email → login).
      storePendingHostListingInviteId(hostInviteMatch[1]);
      const queryIndex = path.indexOf('?');
      if (queryIndex >= 0) {
        const params = new URLSearchParams(path.slice(queryIndex));
        const listingUrl = params.get('listing_url');
        if (listingUrl) {
          storePendingHostListingInviteData({
            invite_id: hostInviteMatch[1],
            listing_url: listingUrl,
            listing_title: params.get('title') || '',
            listing_image: params.get('image') || '',
            listing_location_label: params.get('location') || '',
          });
        }
      }
    }
  } catch { /* storage unavailable — direct navigation still applies */ }
}

function getCapacitor() {
  try {
    if (!window?.Capacitor?.isNativePlatform?.()) return null;
    return window.Capacitor;
  } catch {
    return null;
  }
}

function getAppPlugin() {
  return getCapacitor()?.Plugins?.App || null;
}

function getInstallReferrerPlugin() {
  return getCapacitor()?.Plugins?.InstallReferrer || null;
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((resolve) => setTimeout(() => resolve(null), ms)),
  ]);
}

let launchDeepLink = null;
let capturePromise = null;
let navigateFn = null;
let listenersBound = false;
let lastHandledUrl = null;
let lastHandledAt = 0;

function setLaunchDeepLink(path) {
  // Already honoured in this WebView session (e.g. invite acceptance did
  // window.location.replace and the native getLaunchUrl keeps returning
  // the original invite URL) — do NOT re-store the pending token or we'd
  // bounce the user back to the invite page.
  if (isLaunchDeepLinkConsumed(path)) return;
  launchDeepLink = path;
  try { window.sessionStorage.setItem(LAUNCH_LINK_SS_KEY, path); } catch { /* noop */ }
  rememberDeepLinkPendingIds(path);
}

/** Unconsumed cold-start deep link path (or null). */
export function peekLaunchDeepLink() {
  if (launchDeepLink) return launchDeepLink;
  try { return window.sessionStorage.getItem(LAUNCH_LINK_SS_KEY) || null; } catch { return null; }
}

export function consumeLaunchDeepLink() {
  const path = peekLaunchDeepLink();
  launchDeepLink = null;
  try { window.sessionStorage.removeItem(LAUNCH_LINK_SS_KEY); } catch { /* noop */ }
  return path;
}

async function readLaunchUrl() {
  const appPlugin = getAppPlugin();
  if (!appPlugin || typeof appPlugin.getLaunchUrl !== 'function') return null;
  try {
    const result = await withTimeout(appPlugin.getLaunchUrl(), 3000);
    return extractDeepLinkPath(result?.url);
  } catch {
    return null;
  }
}

async function readInstallReferrer() {
  try {
    if (window.localStorage.getItem(REFERRER_SENTINEL_KEY)) return null;
  } catch {
    return null;
  }
  const plugin = getInstallReferrerPlugin();
  if (!plugin || typeof plugin.getReferrer !== 'function') return null;
  let path = null;
  let responded = false;
  try {
    const result = await withTimeout(plugin.getReferrer(), 4000);
    responded = Boolean(result);
    path = extractReferrerDeepLinkPath(result?.referrer);
  } catch {
    path = null;
  }
  // One-shot: only burn the sentinel once the native side actually answered.
  // A timeout on the very first launch keeps the retry available next boot.
  if (responded) {
    try { window.localStorage.setItem(REFERRER_SENTINEL_KEY, '1'); } catch { /* noop */ }
  }
  return path;
}

/**
 * Start reading the native launch context. Called from index.js BEFORE React
 * mounts so the result is ready (or nearly ready) by the time routing
 * decisions are made. Resolves the deep-link path or null. Idempotent.
 * Instant null on plain web/PWA.
 */
export function captureNativeLaunchContext() {
  if (capturePromise) return capturePromise;
  capturePromise = (async () => {
    if (!getCapacitor()) return null;
    // Read both sources in parallel; the explicit launch intent always wins
    // over the install-time referrer.
    const [fromIntent, fromReferrer] = await Promise.all([
      readLaunchUrl(),
      readInstallReferrer(),
    ]);
    const path = fromIntent || fromReferrer;
    if (path && isLaunchDeepLinkConsumed(path)) {
      // Already honoured earlier in this WebView session. Returning null
      // here keeps `captureNativeLaunchContext()` idempotent so repeated
      // callers (SmartRootRouter, App.js cold-start effect, native
      // initNativeDeepLinks) all agree the launch has no new deep link.
      return null;
    }
    if (path) {
      setLaunchDeepLink(path);
      return path;
    }
    return null;
  })();
  return capturePromise;
}

/** Await the capture with a hard timeout so routing never hangs. */
export function waitForLaunchDeepLink(timeoutMs = 2500) {
  return withTimeout(captureNativeLaunchContext(), timeoutMs);
}

/**
 * Wire up native deep-link navigation. Safe to call on every AppContent
 * mount — listeners bind once but the navigate reference stays fresh.
 * No-op on plain web/PWA and on old APKs without the @capacitor/app plugin.
 */
export function initNativeDeepLinks(navigate) {
  navigateFn = navigate;
  if (listenersBound) return;
  if (!getCapacitor()) return;
  listenersBound = true;

  const handlePath = (target, dedupeKey) => {
    if (!target) return;
    // getLaunchUrl + appUrlOpen can both deliver the same URL on a cold
    // start — dedupe within a short window while still allowing a genuine
    // re-tap of the same link later.
    const now = Date.now();
    if (dedupeKey === lastHandledUrl && now - lastHandledAt < 5000) return;
    lastHandledUrl = dedupeKey;
    lastHandledAt = now;
    if (navigateFn) navigateFn(target);
  };

  // Cold start: launch intent or (first launch after install) referrer.
  // Registered UNCONDITIONALLY on native — the install-referrer path must
  // navigate even if the @capacitor/app plugin is unavailable.
  captureNativeLaunchContext().then((path) => {
    if (!path) return;
    if (isLaunchDeepLinkConsumed(path)) return;
    consumeLaunchDeepLink();
    // SmartRootRouter may have already routed us here (it consumes the
    // buffered link too) — don't push a duplicate history entry.
    try {
      const current = `${window.location.pathname}${window.location.search}`;
      if (current === path) return;
    } catch { /* noop */ }
    handlePath(path, `launch:${path}`);
  });

  // Warm start: app already running when the user taps a link.
  const appPlugin = getAppPlugin();
  if (appPlugin && typeof appPlugin.addListener === 'function') {
    try {
      appPlugin.addListener('appUrlOpen', (event) => {
        const target = extractDeepLinkPath(event?.url);
        if (!target) return;
        rememberDeepLinkPendingIds(target);
        handlePath(target, event.url);
      });
    } catch { /* ignore */ }
  }
}
